import type {CategorySource, Transaction} from "./finance.ts";

export const categoryTaxonomy = [
  "Groceries",
  "Dining",
  "Transport",
  "Fuel",
  "Housing",
  "Utilities",
  "Telecom",
  "Insurance",
  "Health",
  "Fitness",
  "Subscriptions",
  "Shopping",
  "Travel",
  "Entertainment",
  "Education",
  "Childcare",
  "Pets",
  "Fees",
  "Taxes",
  "Income",
  "Savings",
  "Investments",
  "Charity",
  "Cash",
  "Uncategorized"
] as const;
export type Category = (typeof categoryTaxonomy)[number];

const providerCategoryNames: Record<string, string> = {
  BANK_FEES: "Bank fees",
  ENTERTAINMENT: "Entertainment",
  FOOD_AND_DRINK: "Food and drink",
  GENERAL_MERCHANDISE: "Shopping",
  GENERAL_SERVICES: "Services",
  GOVERNMENT_AND_NON_PROFIT: "Government and nonprofit",
  HOME_IMPROVEMENT: "Home improvement",
  INCOME: "Income",
  LOAN_PAYMENTS: "Loan payments",
  MEDICAL: "Health",
  OTHER: "Other",
  PERSONAL_CARE: "Personal care",
  RENT_AND_UTILITIES: "Rent and utilities",
  TRANSPORTATION: "Transport",
  TRANSFER_IN: "Transfers in",
  TRANSFER_OUT: "Transfers out",
  TRAVEL: "Travel",
  UNCATEGORIZED: "Uncategorized"
};

/** Keep provider category codes useful for filtering while showing people readable names. */
export function displayCategoryName(value: string): string {
  const normalized = value.trim();
  if (!normalized) return "Uncategorized";
  const key = normalized.toUpperCase();
  const known = Object.hasOwn(providerCategoryNames, key) ? providerCategoryNames[key] : undefined;
  if (known) return known;
  const words = normalized.split(/[_\s]+/).filter(Boolean);
  return words
    .map((word, index) => {
      const lower = word.toLocaleLowerCase();
      if (index > 0 && ["and", "for", "of", "to"].includes(lower)) return lower;
      if (/^[A-Z0-9]{2,4}$/.test(word)) return word;
      return lower.charAt(0).toLocaleUpperCase() + lower.slice(1);
    })
    .join(" ");
}

export function isCategory(value: string): value is Category {
  return (categoryTaxonomy as readonly string[]).includes(value);
}

interface CategoryRule {
  category: Category;
  patterns: RegExp[];
}

/*
 * Deterministic first pass. These match the counterparty text the institution
 * already sent, so a rule hit is explainable to the person who asks why a
 * charge landed where it did.
 */
const rules: CategoryRule[] = [
  {category: "Groceries", patterns: [/\b(grocer|market|supermarket|aldi|lidl|tesco|carrefour|kroger|safeway|whole foods|trader joe)/i]},
  {category: "Dining", patterns: [/\b(cafe|coffee|restaurant|pizza|burger|sushi|bistro|diner|starbucks|mcdonald|deliveroo|uber eats|doordash|grubhub)/i]},
  {category: "Transport", patterns: [/\b(uber|lyft|taxi|metro|transit|rail|train|bus|parking|toll)/i]},
  {category: "Fuel", patterns: [/\b(shell|bp|exxon|chevron|texaco|total|petrol|gas station|fuel)/i]},
  {category: "Housing", patterns: [/\b(rent|landlord|mortgage|hoa|property management)/i]},
  {category: "Utilities", patterns: [/\b(power|electric|energy|water|gas co|utility|waste|sewer)/i]},
  {category: "Telecom", patterns: [/\b(mobile|wireless|broadband|internet|fiber|verizon|at&t|vodafone|orange|t-mobile)/i]},
  {category: "Insurance", patterns: [/\b(insurance|assurance|geico|allstate|axa|aviva)/i]},
  {category: "Health", patterns: [/\b(pharmacy|clinic|hospital|dental|dentist|optic|medical|doctor|cvs|walgreens)/i]},
  {category: "Fitness", patterns: [/\b(gym|fitness|peloton|yoga|crossfit)/i]},
  {category: "Subscriptions", patterns: [/\b(netflix|spotify|hulu|disney|prime video|adobe|notion|figma|dropbox|icloud|google one|patreon|substack)/i]},
  {category: "Shopping", patterns: [/\b(amazon|ebay|etsy|target|walmart|ikea|zara|h&m|shop|store)/i]},
  {category: "Travel", patterns: [/\b(airline|airways|hotel|motel|hostel|airbnb|booking\.com|expedia|flight|ryanair|easyjet)/i]},
  {category: "Entertainment", patterns: [/\b(cinema|theatre|theater|concert|ticket|steam|playstation|xbox|nintendo)/i]},
  {category: "Education", patterns: [/\b(tuition|university|college|course|udemy|coursera|school)/i]},
  {category: "Childcare", patterns: [/\b(daycare|nursery|childcare|babysit)/i]},
  {category: "Pets", patterns: [/\b(vet|petco|petsmart|pet supply)/i]},
  {category: "Fees", patterns: [/\b(fee|charge|overdraft|interest charge|service charge|atm fee)/i]},
  {category: "Taxes", patterns: [/\b(irs|hmrc|tax|revenue service)/i]},
  {category: "Income", patterns: [/\b(payroll|salary|wage|direct dep|deposit from|dividend|refund|reimburse)/i]},
  {category: "Savings", patterns: [/\b(savings|save|round ?up)/i]},
  {category: "Investments", patterns: [/\b(vanguard|fidelity|schwab|brokerage|invest|etf|coinbase|kraken)/i]},
  {category: "Charity", patterns: [/\b(donation|charity|unicef|red cross|oxfam)/i]},
  {category: "Cash", patterns: [/\b(atm|cash withdrawal|withdrawal)/i]}
];

export interface CategoryVerdict {
  category: Category;
  source: CategorySource;
  confidence: number;
}

/**
 * Classifies the counterparty text. Returns `Uncategorized` rather than a
 * low-confidence guess so a model or the person can fill the gap deliberately.
 */
export function categorizeDescriptor(descriptor: string): CategoryVerdict {
  for (const rule of rules) {
    if (rule.patterns.some((pattern) => pattern.test(descriptor))) {
      return {category: rule.category, source: "rule", confidence: 0.9};
    }
  }
  return {category: "Uncategorized", source: "rule", confidence: 0};
}

/**
 * Precedence: a correction the person made outranks a model, which outranks a
 * rule, which outranks whatever the provider guessed.
 */
const precedence: Record<CategorySource, number> = {user: 4, model: 3, rule: 2, provider: 1};

export function shouldReplaceCategory(existing: Pick<Transaction, "category" | "categorySource">, incoming: CategorySource): boolean {
  if (!existing.category || !existing.categorySource) return true;
  return precedence[incoming] >= precedence[existing.categorySource];
}

type TransactionCategory = Pick<Transaction, "category" | "categorySource" | "categoryConfidence">;

/** A sync may improve a provider/rule category, but never erase a stronger result. */
export function categoryUpdateForSyncedTransaction(existing: TransactionCategory | null, incoming: TransactionCategory): TransactionCategory | null {
  if (!incoming.category || !incoming.categorySource) return null;
  if (existing && !shouldReplaceCategory(existing, incoming.categorySource)) return null;
  return incoming;
}

/** Unique counterparties needing classification, so work scales with merchants not rows. */
export function uncategorizedDescriptors(transactions: readonly Transaction[]): string[] {
  const pending = new Set<string>();
  for (const transaction of transactions) {
    if (transaction.category && transaction.category !== "Uncategorized") continue;
    pending.add(transaction.normalizedDescription);
  }
  return [...pending].sort();
}

export function applyRuleCategories(transactions: readonly Transaction[]): Transaction[] {
  return transactions.map((transaction) => {
    const verdict = categorizeDescriptor(transaction.normalizedDescription || transaction.description);
    if (verdict.confidence === 0) return transaction;
    if (!shouldReplaceCategory(transaction, "rule")) return transaction;
    return {...transaction, category: verdict.category, categorySource: verdict.source, categoryConfidence: verdict.confidence};
  });
}
